import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import HDCartProductSearchComponent from "../HDCartProductSearchComponent";
import { CiDiscount1 } from "react-icons/ci";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useRef, useState } from "react";
import AddressListModal from "../AddressListModal";
import AddAddressModal from "../AddAddressModal";
import { useAddressListAPI } from "@/app/hooks/api_hooks/HDCart/useAddressListAPI";
import { ICartState } from "@/app_redux/reducers/slices/auth/cart_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { BsTruck } from "react-icons/bs";
import { useCartItemsListAPI } from "@/app/hooks/api_hooks/HDCart/useCartItemsListAPI";
import { useHDCouponsListAPI } from "@/app/hooks/api_hooks/HDCart/useHDCouponsListAPI";
import { useDeleteCartItemAPI } from "@/app/hooks/api_hooks/HDCart/useDeleteCartItemAPI";
import { useCartItemAddAPI } from "@/app/hooks/api_hooks/HDCart/useCartItemAddAPI";
import {
  IHDCartCheckoutPostBody,
  IHDCartItemsAddPutBody,
  IHDCartOrderCreationFilter,
  IHDItemSearchResponseData,
} from "@/api_framework/api_modals/Cart";
import CartRow from "../CartRow";
import { useLoader } from "@/app/hooks/useLoader";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import OrderPlacedModal from "../OrderPlacedModal";
import { useOrderCreateAPI } from "@/app/hooks/api_hooks/HDCart/useOrderCreateAPI";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { toast } from "react-hot-toast";
import { useHDCartCheckoutAPI } from "@/app/hooks/api_hooks/HDCart/useHDCartCheckoutAPI";
import { BiError } from "react-icons/bi";
import { TrashIcon } from "@heroicons/react/24/outline";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { MdCurrencyRupee } from "react-icons/md";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

export interface IHDCartPageProps {
  cart_level_coupon_id: string;
  cod: boolean;
}

const HDCartPage = () => {
  const { getRouteKey } = useRouter();
  const [activeModal, setActiveModal] = useState<"addressList" | "addAddress" | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { addressList, cartItemsList, hdCouponsList, activeCartDetails } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState
    .state as ICartState;
  const cartLevelCouponOptions = useSelectMenuReducer(hdCouponsList, "coupon_name", "coupon_id");
  const { customerId, customerDetails, petId } = routeState.state as ICustomerProfileState;
  const fetchAddressList = useAddressListAPI();
  const fetchCartItems = useCartItemsListAPI();
  const min = 1;
  const fetchHDCouponsList = useHDCouponsListAPI();
  const deleteCartItem = useDeleteCartItemAPI();
  const addCartItem = useCartItemAddAPI();
  const isCartTabeLoading = useLoader("isCustomerHDCartTableInProgress");
  const { execute: fetchOrderApi } = useOrderCreateAPI();
  const configuration = useConfiguration();
  const [cartCheckoutKey, setCartCheckoutKey] = useState<string | null>(null);
  const [hasLoadedStoredAddress, _setHasLoadedStoredAddress] = useState(false);
  const { execute: cartCheckout } = useHDCartCheckoutAPI();
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [orderPlacedOpen, setOrderPlacedOpen] = useState(false);
  const [prevCustomerId, setPrevCustomerId] = useState<string | null>(null);
  const [hasShownAddressModal, setHasShownAddressModal] = useState(
    () => sessionStorage.getItem("hasShownAddressModal") === "true",
  );
  const [addressListLoaded, setAddressListLoaded] = useState(false);
  const availableCartItems =
    cartItemsList?.main_cart_items?.filter((item) => item?.availability?.toLowerCase() !== "unavailable") || [];
  const { mappedClinicName, mappedClinicId } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const [isDeliveryRemoved, setIsDeliveryRemoved] = useState(false);
  const { execute: logAPI } = useClickLogAPI();

  const formHook = useForm<IHDCartPageProps>({
    defaultValues: {
      cart_level_coupon_id: "",
    },
  });

  const cartLevelCouponId = formHook.watch("cart_level_coupon_id");

  useEffect(() => {
    if (cartItemsList?.cart_level_coupon_id) {
      console.log(cartItemsList, "cartItemsList");
      formHook.setValue("cart_level_coupon_id", cartItemsList?.cart_level_coupon_id);
    }
  }, [cartItemsList]);

  useEffect(() => {
    if (customerId) {
      fetchAddressList(customerId, null, null, mappedClinicId);
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    fetchAddressList(customerId, null, null, mappedClinicId).then(() => {
      setAddressListLoaded(true);
    });
  }, [customerId]);

  const generateCartKey = useCallback(
    () =>
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    [],
  );

  useEffect(() => {
    if (!cartItemsList?.cart_id) {
      setCartCheckoutKey(null);
      return;
    }
    const storageKey = `hd_cart_checkout_key_${cartItemsList.cart_id}`;
    let storedKey = sessionStorage.getItem(storageKey);
    if (!storedKey) {
      storedKey = generateCartKey();
      sessionStorage.setItem(storageKey, storedKey);
    }
    setCartCheckoutKey(storedKey);
  }, [cartItemsList?.cart_id, generateCartKey]);

  const hasResolvedInitialAddressRef = useRef(false);

  useEffect(() => {
    if (!addressListLoaded || hasResolvedInitialAddressRef.current) return;
    hasResolvedInitialAddressRef.current = true;
    const storedAddress = localStorage.getItem(`selectedAddress_${customerId}`);
    if (storedAddress) {
      setSelectedAddress(JSON.parse(storedAddress));
      setActiveModal(null);
    } else {
      if (addressList && addressList.length > 0) {
        setActiveModal("addressList");
      } else {
        setActiveModal("addAddress");
      }
    }
  }, [addressListLoaded, addressList, customerId]);

  useEffect(() => {
    if (!hasLoadedStoredAddress) return;
    if (!selectedAddress && !hasShownAddressModal) {
      if (addressList && addressList.length > 0) {
        setActiveModal("addressList");
      } else if (addressList && addressList.length === 0) {
        setActiveModal("addAddress");
      }
      sessionStorage.setItem("hasShownAddressModal", "true");
      setHasShownAddressModal(true);
    }
  }, [addressList, selectedAddress, hasLoadedStoredAddress, hasShownAddressModal]);

  useEffect(() => {
    if (!customerId) return;
    if (prevCustomerId && prevCustomerId !== customerId) {
      setSelectedAddress(null);
      setActiveModal(null);
      setAddressListLoaded(false);
      hasResolvedInitialAddressRef.current = false;
    }
    setPrevCustomerId(customerId);
  }, [customerId]);

  const fetchCartItemsWithCoupon = (customerId: string, pincode: string, couponId?: string) => {
    return fetchCartItems(customerId, pincode, "hd", couponId || "");
  };

  const handleApplyOrRemoveCoupon = () => {
    if (!customerId || !selectedAddress?.pincode) return;
    if (isCouponApplied) {
      formHook.setValue("cart_level_coupon_id", "");
      setIsCouponApplied(false);
      fetchCartItemsWithCoupon(customerId, selectedAddress.pincode, "remove");
    } else {
      const selectedCouponId = formHook.getValues("cart_level_coupon_id");
      if (!selectedCouponId) {
        toast.error("Please select a coupon to apply.");
        return;
      }
      fetchCartItemsWithCoupon(customerId, selectedAddress.pincode, selectedCouponId);
      setIsCouponApplied(true);
    }
  };

  useEffect(() => {
    if (selectedAddress?.pincode && customerId) {
      fetchCartItemsWithCoupon(customerId, selectedAddress?.pincode);
    }
  }, [selectedAddress, customerId]);

  useEffect(() => {
    if (cartItemsList && customerId && selectedAddress?.pincode) {
      fetchHDCouponsList(customerId, selectedAddress?.pincode);
    }
  }, [cartItemsList, customerId, selectedAddress]);

  useEffect(() => {
    if (cartItemsList?.billing_details?.cod_text) {
      formHook.setValue("cod", false);
    }
  }, [cartItemsList?.billing_details?.cod_text, formHook]);

  const handleDeleteItem = async (item: any) => {
    if (!customerId || !selectedAddress?.pincode) return;
    const payload = {
      customer_id: customerId,
      pincode: selectedAddress?.pincode,
      vetic_variant_id: item?.vetic_variant_id,
      unique_row_id: item?.unique_row_id,
    };
    const res = await deleteCartItem.execute(payload);
    if (res?.status) {
      fetchCartItemsWithCoupon(customerId, selectedAddress.pincode, isCouponApplied ? cartLevelCouponId : undefined);
    }
  };

  const updateCartItem = async (item: any, quantity: number, couponId: string, isSearchClick: boolean) => {
    if (!customerId || !selectedAddress?.pincode) return;
    const availability =
      item?.stock_qty_with_text?.toLowerCase() === "unavailable" ? "unavailable" : item?.stock_qty_with_text;
    const payload: IHDCartItemsAddPutBody = {
      customer_id: customerId,
      pincode: selectedAddress.pincode,
      main_cart_items: [
        {
          vetic_variant_id: item?.id || item?.vetic_variant_id,
          availability,
          quantity: quantity,
          unit_price: Number(item?.unit_price),
          unique_row_id: item?.unique_row_id || "",
          coupon_id: couponId || "",
        },
      ],
      is_search_click: isSearchClick,
    };
    const res = await addCartItem.execute(payload, "hd");
    if (res?.status) {
      fetchCartItemsWithCoupon(customerId, selectedAddress.pincode, isCouponApplied ? cartLevelCouponId : undefined);
    }
  };

  const handleSelectItem = async (data: IHDItemSearchResponseData) => {
    if (!data) return;
    if (data.name?.length < 3) {
      toast.error("Please type at least 3 characters to add item.");
      return;
    }
    await updateCartItem(data, 1, "", true);
  };

  const handleCheckout = async () => {
    if (!selectedAddress || !cartItemsList) return;
    const razorpayEnv = configuration.env.mode === "development" ? "dev" : "prod";
    const checkoutKey = cartCheckoutKey || generateCartKey();
    if (!cartCheckoutKey && cartItemsList.cart_id) {
      const storageKey = `hd_cart_checkout_key_${cartItemsList.cart_id}`;
      sessionStorage.setItem(storageKey, checkoutKey);
      setCartCheckoutKey(checkoutKey);
    }
    const payload: IHDCartOrderCreationFilter = {
      user_id: customerId,
      created_by: customerDetails?.email,
      address_id: selectedAddress?.address_id,
      razorpay_env: razorpayEnv,
      pincode: selectedAddress?.pincode,
      final_price: cartItemsList?.billing_details?.net_total_after_tax,
      payment_partner: "razorpay",
      idemp_key: checkoutKey,
      source_clinic: mappedClinicId,
    };
    if (activeCartDetails?.booking_id) {
      payload.appointment_id = activeCartDetails.booking_id;
    }
    const body: IHDCartCheckoutPostBody = {
      cart_id: cartItemsList?.cart_id,
    };
    const paymentMethodId = formHook.watch("cod") ? 1 : 2;
    try {
      const [orderRes, checkoutRes] = await Promise.all([fetchOrderApi(payload, paymentMethodId), cartCheckout(body)]);
      if (orderRes?.status && checkoutRes?.status) {
        setOrderPlacedOpen(true);
        if (formHook.watch("cod")) {
          toast.success("COD selected. Order placed successfully!");
        }
        callLogAPILabCheckout(cartItemsList?.cart_id);
      } else {
        if (!orderRes?.status) toast.error("Order creation failed, please try again.");
        if (!checkoutRes?.status) toast.error("Cart checkout failed, please try again.");
      }
    } catch (error) {
      toast.error("Checkout process failed. Please try again.");
    }
  };

  const handleRemove = () => {
    if (!customerId || !selectedAddress?.pincode) return;
    fetchCartItems(customerId, selectedAddress.pincode, "hd", cartLevelCouponId || "", true);
    setIsDeliveryRemoved(true);
  };

  const callLogAPIBase = (item: any, event_type: "HD_CART_CHECKOUT") => {
    const logBody: IClickLogBody = {
      event_type: event_type,
      scr_id: "EMR",
      client_id: customerId,
      cart_id: cartItemsList?.cart_id,
      pet_id: petId,
      emr_cart_items: item,
      address_id: item,
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const callLogAPILabCheckout = (item: any) => callLogAPIBase(item, "HD_CART_CHECKOUT");

  return (
    <div className="flex flex-col gap-3 pb-6 lg:flex-row lg:items-start">
      <div className="mt-2 min-w-0 flex-1 rounded-lg bg-white p-3 shadow-md">
        <div className="font-semibold text-[14px]">Add Items in Cart</div>
        <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
          <div className="md:w-3/5">
            <HDCartProductSearchComponent
              placeholder="Search Food & Accessories, Medicine and Others"
              onSelectItem={handleSelectItem}
              inputContainerClassName="w-4/5"
              pincode={selectedAddress?.pincode}
              customerId={customerId}
              petId={petId}
              mappedClinicID={mappedClinicId}
              mappedClinicName={mappedClinicName}
              userLogInfo={userLogInfo}
              cartItemsList={cartItemsList}
            />
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <CiDiscount1
                className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-[#6c727f]"
                size={16}
              />
              <SearchableSelectMenu
                showDropdownIcon
                selectItems={cartLevelCouponOptions}
                fieldError={formHook?.formState?.errors?.cart_level_coupon_id}
                errorMessages={[]}
                register={formHook.register("cart_level_coupon_id", { required: false })}
                containerClassName="w-full"
                className="!pl-9"
                placeholder="Select Cart Level Coupon"
                onSelectItem={(item) => {
                  formHook.setValue("cart_level_coupon_id", item.id);
                }}
                defaultSelected={cartLevelCouponOptions?.find(
                  (item) => item.id === formHook.watch("cart_level_coupon_id"),
                )}
              />
            </div>
            <button
              className={`h-[38px] shrink-0 rounded-lg px-5 text-xs font-medium text-white
              ${cartLevelCouponId || isCouponApplied ? "bg-black cursor-pointer" : "bg-gray-400 opacity-50 cursor-not-allowed"}
            `}
              type="button"
              disabled={!cartLevelCouponId && !isCouponApplied}
              onClick={handleApplyOrRemoveCoupon}
            >
              {isCouponApplied ? "Remove" : "Apply"}
            </button>
          </div>
        </div>
        <div className="w-full mt-6 p-2 bg-[#F05B72] rounded-2xl text-white shadow-[inset_0_4px_12px_rgba(255,255,255,0.7)]">
          <div className="flex flex-wrap justify-between items-center gap-2 mt-2 mb-4">
            <div className="flex items-center gap-2">
              <h2 className="flex items-center gap-2 font-semibold text-lg">
                <BsTruck className="w-5 h-5 ml-2 text-white" />{" "}
                <span className="font-bold text-[14px] mr-2">Home Delivery Items</span>
              </h2>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="bg-white text-xs md:text-sm px-2 py- rounded-md">
                  <p className="text-gray-600 text-[10px]">{cartItemsList?.no_of_items} items</p>
                </span>
                <span className="bg-white text-xs md:text-sm px-2 py- rounded-md">
                  <p className="text-gray-600 text-[10px]">{cartItemsList?.delivery_time}</p>
                </span>
              </div>
            </div>
            <span className="mr-2 font-bold text-[14px]">Total ₹ {cartItemsList?.total_price_of_items}</span>
          </div>
          <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
            {isCartTabeLoading ? (
              <TableSkeleton />
            ) : (
              cartItemsList?.main_cart_items?.length > 0 && (
                <table className="w-full text-gray-800 text-sm md:text-base">
                  <thead className="bg-gray-50 border-b">
                    <tr className="sm:text-[12px] text-[12px]">
                      <th className="p-3 text-left font-medium">Goods</th>
                      <th className="p-3 text-left font-medium">Quantity</th>
                      <th className="p-3 text-left font-medium">Unit Price</th>
                      <th className="p-3 text-left font-medium">Gross</th>
                      <th className="p-3 text-left font-medium">Coupon</th>
                      <th className="p-3 text-left font-medium">Net</th>
                      <th className="p-3 text-center font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableCartItems.map((item, id) => (
                      <CartRow
                        key={id}
                        item={item}
                        min={min}
                        updateCartItem={updateCartItem}
                        customerId={customerId}
                        petId={petId}
                        cartItemsList={cartItemsList}
                        mappedClinicId={mappedClinicId}
                        mappedClinicName={mappedClinicName}
                        userLogInfo={userLogInfo}
                        selectedAddress={selectedAddress}
                        isCouponApplied={isCouponApplied}
                        cartLevelCouponId={cartLevelCouponId}
                        fetchCartItemsWithCoupon={fetchCartItemsWithCoupon}
                      />
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
        {activeModal === "addressList" && (
          <AddressListModal
            isAddressOpen={true}
            addressSelectionTitle="Select Home Delivery Address"
            onClose={() => setActiveModal(null)}
            payloadData={addressList}
            openAddAddress={(addressId?: string) => {
              setSelectedAddressId(addressId || null);
              setActiveModal("addAddress");
            }}
            onSelectAddress={(address) => {
              setSelectedAddress(address);
              localStorage.setItem(`selectedAddress_${customerId}`, JSON.stringify(address));
              if (customerId && address?.pincode) {
                fetchCartItemsWithCoupon(customerId, address.pincode);
              }
              setActiveModal(null);
            }}
          />
        )}
        {activeModal === "addAddress" && (
          <AddAddressModal
            source="cart"
            addAddressTitle="Add Home Delivery Address"
            addressId={selectedAddressId}
            onClose={() => setActiveModal(null)}
            onAddressAdded={(newAddress) => {
              if (!newAddress) return;
              setSelectedAddressId(newAddress.address_id);
              setSelectedAddress(newAddress);
              localStorage.setItem(`selectedAddress_${customerId}`, JSON.stringify(newAddress));
              if (customerId && newAddress?.pincode) {
                fetchCartItemsWithCoupon(customerId, newAddress.pincode);
              }
              setActiveModal(null);
            }}
          />
        )}
      </div>
      <div className="w-full shrink-0 lg:sticky lg:top-2 lg:w-[380px]">
        {cartItemsList?.out_of_stock_items?.length > 0 && (
          <div className="mt-2 rounded-lg bg-white px-5 py-3 shadow-md">
            <div className="flex items-center gap-2">
              <BiError color="red" />
              <div className="font-semibold text-[14px] text-red-500">Out of Stock Items</div>
            </div>
            {cartItemsList?.out_of_stock_items?.map((item, index) => (
              <ul key={index}>
                <div className="text-black text-sm mt-2 list-disc list-inside flex justify-between">
                  <li>{item?.name}</li>
                  <button onClick={() => handleDeleteItem(item)} className="text-gray-500 hover:text-red-500">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </ul>
            ))}
          </div>
        )}
        <div className="mt-2 rounded-lg bg-white px-5 py-4 shadow-md">
          <div className="font-semibold text-[14px]">Price Breakup</div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[#6d717f] text-sm font-medium">Total MRP</p>
            <p className="text-[#6d717f] text-sm flex items-center">
              <span>
                <MdCurrencyRupee />
              </span>
              {cartItemsList?.billing_details?.total_gross_price}
            </p>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[#6d717f] text-sm font-medium">Discount</p>
            <p className="text-[#07863d] text-sm flex items-center font-medium">
              -
              <span>
                <MdCurrencyRupee />
              </span>
              {cartItemsList?.billing_details?.total_discount}
            </p>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="flex gap-2 text-sm font-medium text-[#6d717f]">
              Delivery Price
              {cartItemsList?.free_delivery !== true && !isDeliveryRemoved && (
                <span className="text-red-500 font-medium underline cursor-pointer" onClick={handleRemove}>
                  Remove
                </span>
              )}
            </p>
            {cartItemsList?.free_delivery === true ? (
              <span className="text-sm font-medium text-[#07863d]">Free</span>
            ) : (
              <p className="flex items-center text-sm text-[#6d717f]">
                <MdCurrencyRupee />
                {cartItemsList?.billing_details?.delivery_charges}
              </p>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[#6d717f] text-sm font-medium">Total Tax</p>
            <p className="text-[#6d717f] text-sm flex items-center">
              <span>
                <MdCurrencyRupee />
              </span>
              {cartItemsList?.billing_details?.total_tax}
            </p>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-dashed border-gray-200 pt-3">
            <p className="text-sm font-semibold text-black">Net Total</p>
            <p className="flex items-center text-sm font-semibold text-black">
              <span>
                <MdCurrencyRupee />
              </span>
              {cartItemsList?.billing_details?.net_total_after_tax}
            </p>
          </div>
          <div className="rounded-lg bg-[#f4f4f4] p-3 mt-5">
            {selectedAddress && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-medium">Delivery Address</div>
                  <div
                    className={`rounded-2xl py-1 px-3 text-[10px] font-medium
                        ${selectedAddress?.label === "Home" ? "bg-[#e7f8ef] text-[#07863d]" : ""}
                        ${selectedAddress?.label === "Office" ? "bg-[#f8e7d6] text-[#a75610]" : ""}
                        ${selectedAddress?.label === "Others" ? "bg-[#eff1ff] text-[#3969ff]" : ""}`}
                  >
                    {selectedAddress?.label}
                  </div>
                  {selectedAddress.previously_ordered && (
                    <div className="rounded-md bg-[#dedede] px-2 py-1 text-center text-[10px] text-[#666666]">
                      Last Delivered
                    </div>
                  )}
                </div>
                <div className="mt-3 break-words text-xs text-[#6a717e]">{selectedAddress?.address_1}</div>
                <div className="mt-1 break-words text-xs text-[#6a717e]">{selectedAddress?.address_2}</div>
              </>
            )}
            <div className="flex gap-2 items-center mt-2">
              <BsTruck className="w-5 h-5 text-black" />
              <div className="text-[#6a717e] text-xs">
                Delivered by{" "}
                <span className="text-black font-medium">{cartItemsList?.billing_details?.delivery_status}</span>
              </div>
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setActiveModal("addressList")}
                className="w-full rounded-lg bg-white px-4 py-[9px] text-sm font-medium text-[#0069fe] shadow-sm"
              >
                Change Address
              </button>
            </div>
          </div>
          <div className="mt-3 rounded-lg bg-[#fafafa] p-3">
            <div className="flex items-center">
              <label
                className={`flex items-center text-sm ${!cartItemsList?.billing_details?.is_cod_available ? "cursor-not-allowed text-gray-400" : ""
                  }`}
              >
                <input
                  type="checkbox"
                  disabled={
                    !cartItemsList?.billing_details?.is_cod_available || !!cartItemsList?.billing_details?.cod_text
                  }
                  checked={formHook.watch("cod") || false}
                  onChange={() => {
                    if (cartItemsList?.billing_details?.cod_text) return;
                    formHook.setValue("cod", !formHook.getValues("cod"));
                  }}
                  className="form-radio cursor-pointer"
                />
                <span className="ml-2">
                  {cartItemsList?.billing_details?.is_cod_available ? "Cash on Delivery" : "COD Not Available"}
                </span>
              </label>
            </div>
            {cartItemsList?.billing_details?.cod_text && (
              <p className="pl-6 pt-1 text-xs text-red-500">{cartItemsList?.billing_details?.cod_text}</p>
            )}
          </div>
          {isCartTabeLoading ? (
            <div className="mt-5 flex justify-center">
              <CircleSpinnerLoader h="20" w="20" />
            </div>
          ) : (
            <div className="mt-5">
              <button
                type="button"
                onClick={handleCheckout}
                disabled={!availableCartItems.length || isCartTabeLoading}
                className={`w-full rounded-lg px-4 py-[9px] text-sm font-medium shadow-sm ${availableCartItems.length ? "bg-[#07863d] text-white" : "bg-gray-400 text-gray-200 cursor-not-allowed"
                  }`}
              >
                {formHook.watch("cod") ? "Check Out" : "Collect Payment & Check Out"}
              </button>
            </div>
          )}
        </div>
      </div>
      <OrderPlacedModal open={orderPlacedOpen} cod={formHook.watch("cod")} />
    </div>
  );
};

export default HDCartPage;
